/**
 * AuditResultsTable Component
 * TanStack Table v8 implementation with HTML table elements for audit results
 * NO MUI Table components - uses standard HTML <table>, <thead>, <tbody>, <tr>, <td>
 *
 * @module MetricDataDevTool/components/AuditResultsTable
 */

import React, { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender
} from '@tanstack/react-table';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Stack,
  Chip
} from '@mui/material';
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as ValidIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { getMetricDisplayName } from '../utils/metricDefinitions';
import UuidRecordsList from './UuidRecordsList';

/**
 * AuditResultsTable component
 * @param {Object} props - Component props
 * @param {Array} props.results - Audit results array
 */
export default function AuditResultsTable({ results = [] }) {
  const [sorting, setSorting] = useState([]);
  const [expanded, setExpanded] = useState({});

  /**
   * Define table columns
   */
  const columns = useMemo(
    () => [
      {
        id: 'expander',
        header: '',
        cell: ({ row }) => (
          row.original.uuidCount > 0 ? (
            <IconButton
              size="small"
              onClick={row.getToggleExpandedHandler()}
              sx={{ p: 0 }}
            >
              {row.getIsExpanded() ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          ) : null
        ),
        size: 40
      },
      {
        accessorKey: 'metricName',
        header: 'Metric',
        cell: ({ getValue }) => {
          const metricName = getValue();
          const displayName = getMetricDisplayName(metricName);
          return (
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {metricName}
              </Typography>
            </Box>
          );
        }
      },
      {
        accessorKey: 'date',
        header: 'Date',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()}</Typography>
        ),
        size: 120
      },
      {
        accessorKey: 'expected',
        header: 'Expected',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()}</Typography>
        ),
        size: 100
      },
      {
        accessorKey: 'actual',
        header: 'Actual',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()}</Typography>
        ),
        size: 100
      },
      {
        accessorKey: 'variance',
        header: 'Variance',
        cell: ({ getValue, row }) => {
          const variance = getValue();
          const isValid = row.original.isValid;
          return (
            <Chip
              label={variance > 0 ? `+${variance}` : variance}
              size="small"
              color={isValid ? 'default' : 'error'}
              sx={{ minWidth: 60 }}
            />
          );
        },
        size: 100
      },
      {
        accessorKey: 'isValid',
        header: 'Status',
        cell: ({ getValue }) => {
          const isValid = getValue();
          return isValid ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
              <ValidIcon fontSize="small" />
              <Typography variant="body2">Valid</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
              <WarningIcon fontSize="small" />
              <Typography variant="body2">Discrepancy</Typography>
            </Box>
          );
        },
        size: 120
      },
      {
        accessorKey: 'uuidCount',
        header: 'UUID Records',
        cell: ({ getValue }) => {
          const count = getValue();
          return count > 0 ? (
            <Chip
              label={`${count} UUID`}
              size="small"
              color="warning"
              sx={{ minWidth: 80 }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          );
        },
        size: 120
      },
      {
        accessorKey: 'compositeCount',
        header: 'Composite',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()}</Typography>
        ),
        size: 100
      }
    ],
    []
  );

  /**
   * Initialize TanStack Table
   */
  const table = useReactTable({
    data: results,
    columns,
    state: {
      sorting,
      expanded
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => row.original.uuidCount > 0,
    initialState: {
      pagination: {
        pageSize: 25
      }
    }
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Audit Results
      </Typography>

      {/* Table */}
      <Box sx={{ overflowX: 'auto', mb: 2 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem'
          }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderBottom: '2px solid #e0e0e0',
                      backgroundColor: '#f5f5f5',
                      fontWeight: 600,
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      width: header.column.columnDef.size || 'auto'
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: ' 🔼',
                      desc: ' 🔽'
                    }[header.column.getIsSorted()] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: row.original.isValid ? 'transparent' : '#fff3e0'
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: '12px 16px'
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
                {/* Expanded row for UUID records */}
                {row.getIsExpanded() && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{
                        padding: '16px',
                        backgroundColor: '#fafafa',
                        borderBottom: '1px solid #e0e0e0'
                      }}
                    >
                      <UuidRecordsList records={row.original.uuidRecords} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Box>

      {/* Pagination Controls */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <Select
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            size="small"
            sx={{ minWidth: 80 }}
          >
            {[10, 25, 50, 100].map((pageSize) => (
              <MenuItem key={pageSize} value={pageSize}>
                {pageSize}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Page {currentPage} of {pageCount}
        </Typography>

        <Stack direction="row" spacing={0.5}>
          <IconButton
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            size="small"
          >
            <FirstPageIcon />
          </IconButton>
          <IconButton
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            size="small"
          >
            <PrevIcon />
          </IconButton>
          <IconButton
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            size="small"
          >
            <NextIcon />
          </IconButton>
          <IconButton
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={!table.getCanNextPage()}
            size="small"
          >
            <LastPageIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}
